import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getAffiliateById, updateAffiliate } from '../../services/companyAuthService';

const EditAffiliate = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    affiliateType: 'individual',
    companyName: '',
    instagramId: '',
    paymentInfo: {
      method: 'bank',
      bankName: '',
      accountNumber: '',
      ifscCode: '',
      upiId: ''
    }
  });

  useEffect(() => {
    fetchAffiliate();
  }, [id]);

  const groupCampaigns = (campaigns) => {
    const groupedMap = new Map();
    
    campaigns.forEach(campaignItem => {
      const campaignId = campaignItem.campaign._id;
      if (!groupedMap.has(campaignId)) {
        groupedMap.set(campaignId, {
          ...campaignItem,
          status: campaignItem.status
        });
      }
    });

    return Array.from(groupedMap.values());
  };
  const fetchAffiliate = async () => {
    try {
      setLoading(true);
      const data = await getAffiliateById(id);
      if (data.campaigns) {
        data.campaigns = groupCampaigns(data.campaigns);
      }
      setFormData({
        firstName: data.firstName || '',
        lastName: data.lastName || '',
        email: data.email || '',
        affiliateType: data.affiliateType || 'individual',
        companyName: data.companyName || '',
        instagramId: data.instagramId || '',
        paymentInfo: {
          method: data.paymentInfo?.method || 'bank',
          bankName: data.paymentInfo?.bankName || '',
          accountNumber: data.paymentInfo?.accountNumber || '',
          ifscCode: data.paymentInfo?.ifscCode || '',
          upiId: data.paymentInfo?.upiId || ''
        }
      });
    } catch (error) {
      console.error('Error fetching affiliate:', error);
      toast.error(error.message || 'Failed to fetch affiliate details');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('paymentInfo.')) {
      const paymentField = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        paymentInfo: {
          ...prev.paymentInfo,
          [paymentField]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const response = await updateAffiliate(id, formData);
      
      if (response) {
        toast.success('Affiliate updated successfully');
        // Fetch updated data
        await fetchAffiliate();
        navigate('/company/dashboard/affiliates/');
      } else {
        throw new Error('Failed to update affiliate');
      }
    } catch (error) {
      console.error('Error updating affiliate:', error);
      toast.error(error.message || 'Failed to update affiliate');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <>
      
        <div className="flex justify-center items-center min-h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
        </div>
     
      </>
    );
  }

  return (
    <>
      <div className="flex flex-col lg:flex-row min-h-full bg-gray-100">
        <div className="flex-1 p-2 md:p-4 lg:p-6">
          <div className="bg-white shadow-md rounded-lg p-4 lg:p-6 mb-10 lg:mb-0 ">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Edit Affiliate</h2>
              <button
                onClick={() => navigate('/company/dashboard/affiliates/')}
                className="inline-flex items-center px-2 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 hover:border-orange-200 transition-colors duration-200"
              >
                Back
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Profile Information */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold mb-4">Profile Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">First Name</label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Last Name</label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Type</label>
                    <select
                      name="affiliateType"
                      value={formData.affiliateType}
                      // onChange={handleChange}
                      onChange={(e) => {
                        // block selecting anything other than influencer during edit
                        if (e.target.value !== 'influencer') {
                          return;
                        }
                        handleChange(e);
                      }}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500"
                    >
                      <option value="influencer">Influencer</option>
                      {/* <option value="individual">Individual</option>
                      <option value="company">Company</option> */}
                    </select>
                  </div>
                  {formData.affiliateType === 'company' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Company Name</label>
                      <input
                        type="text"
                        name="companyName"
                        value={formData.companyName}
                        onChange={handleChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500"
                      />
                    </div>
                  )}
                  {formData.affiliateType === 'individual' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Instagram ID</label>
                      <input
                        type="text"
                        name="instagramId"
                        value={formData.instagramId}
                        onChange={handleChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500"
                      />
                    </div>
                  )}
                  {formData.affiliateType === 'influencer' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Instagram ID</label>
                      <input
                        type="text"
                        name="instagramId"
                        value={formData.instagramId}
                        onChange={handleChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Payment Information */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold mb-4">Payment Information</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Payment Method</label>
                    <select
                      name="paymentInfo.method"
                      value={formData.paymentInfo.method}
                      onChange={handleChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500"
                    >
                      <option value="bank">Bank Transfer</option>
                      <option value="upi">UPI</option>
                    </select>
                  </div>

                  {formData.paymentInfo.method === 'bank' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Bank Name</label>
                        <input
                          type="text"
                          name="paymentInfo.bankName"
                          value={formData.paymentInfo.bankName}
                          onChange={handleChange}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Account Number</label>
                        <input
                          type="text"
                          name="paymentInfo.accountNumber"
                          value={formData.paymentInfo.accountNumber}
                          onChange={handleChange}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">IFSC Code</label>
                        <input
                          type="text"
                          name="paymentInfo.ifscCode"
                          value={formData.paymentInfo.ifscCode}
                          onChange={handleChange}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500"
                        />
                      </div>
                    </>
                  )}

                  {formData.paymentInfo.method === 'upi' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">UPI ID</label>
                      <input
                        type="text"
                        name="paymentInfo.upiId"
                        value={formData.paymentInfo.upiId}
                        onChange={handleChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500"
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={() => navigate('/company/dashboard/affiliates/')}
                  className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
     
    </>
  );
};

export default EditAffiliate; 